import _LinearProgress2 from "@mui/material/LinearProgress";
var _ArrowRight, _LinearProgress;
import React from 'react';
import { array, arrayOf, bool, func, number, object, oneOf, shape, string } from 'prop-types';
import clsx from 'clsx';
import { decomposeColor, recomposeColor, emphasize, styled } from '@mui/material/styles';
import { ArrowRight } from 'mosaic-react-icons';
import ButtonBase, { buttonBaseClasses } from '@mui/material/ButtonBase';
import SovosTypography from '../../sovos-typography';
import SovosCheckbox from '../../sovos-checkbox';
import SectionDivider from './SectionDivider';
import SovosIconButton from '../../sovos-icon-button';
import SovosFixedWidthText from '../../sovos-fixed-width-text';
import itemTypes from '../../internals/utils/itemTypes';
import { getFocusStyles } from '../../internals/utils';
import useMosaicTranslation from '../../internals/i18n/useMosaicTranslation';
const getSelectedHoveredColor = palette => {
  const selected = decomposeColor(palette.action.selected);
  const opacity = selected.values[3];
  if (opacity) {
    const newOpacity = opacity + palette.action.hoverOpacity;
    selected.values[3] = newOpacity < 1 ? newOpacity : 1;
    return recomposeColor(selected);
  }
  return emphasize(palette.action.selected, palette.action.hoverOpacity);
};
const ListItem = styled('li')({
  position: 'relative'
});
const ItemButton = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== 'isExpandable'
})(_ref => {
  let {
    isExpandable,
    theme: {
      palette,
      spacing,
      typography
    }
  } = _ref;
  return {
    ...typography.body1,
    cursor: 'pointer',
    display: 'flex',
    margin: 0,
    minHeight: spacing(4.5),
    paddingBottom: 0,
    paddingLeft: isExpandable ? 0 : spacing(2),
    paddingRight: spacing(2),
    paddingTop: 0,
    textAlign: 'left',
    width: '100%',
    '&:hover, &:focus, &:focus-visible': {
      backgroundColor: palette.action.hover
    },
    ["&:focus-visible, &.".concat(buttonBaseClasses.focusVisible)]: {
      backgroundColor: palette.action.hover,
      ...getFocusStyles({
        offset: -2
      })
    },
    '&.sovosTreeListItem__ItemButton--selected': {
      backgroundColor: palette.action.selected,
      ["&.".concat(buttonBaseClasses.focusVisible)]: {
        backgroundColor: getSelectedHoveredColor(palette)
      },
      '&:hover, &:focus, &:focus-visible': {
        backgroundColor: getSelectedHoveredColor(palette)
      }
    },
    '&.sovosTreeListItem__ItemButton--noHover': {
      '&:hover': {
        cursor: 'default',
        backgroundColor: 'transparent'
      }
    },
    '&.sovosTreeListItem__ItemButton--disabled': {
      color: palette.text.disabled
    }
  };
});
const ItemText = styled('span', {
  shouldForwardProp: prop => prop !== 'isExpandable'
})(_ref2 => {
  let {
    isExpandable,
    theme: {
      spacing
    }
  } = _ref2;
  return {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    height: '100%',
    marginLeft: isExpandable ? spacing(5) : 0,
    minWidth: 0,
    padding: 0
  };
});
const NestedItems = styled('ul')(_ref3 => {
  let {
    theme: {
      spacing
    }
  } = _ref3;
  return {
    listStyle: 'none',
    paddingLeft: spacing(4.5)
  };
});
const ToggleButton = styled(SovosIconButton)(_ref4 => {
  let {
    theme: {
      palette,
      spacing
    }
  } = _ref4;
  return {
    color: palette.text.primary,
    cursor: 'pointer',
    left: spacing(0.5),
    position: 'absolute',
    top: spacing(0.25),
    transition: 'transform 0.3s',
    '&[aria-expanded="true"]': {
      transform: 'rotate(90deg)'
    },
    zIndex: 4
  };
});
const Loading = styled('div')(_ref5 => {
  let {
    theme: {
      spacing
    }
  } = _ref5;
  return {
    padding: spacing(4),
    textAlign: 'center',
    '& p': {
      marginTop: spacing()
    }
  };
});
const TreeListItem = _ref6 => {
  var _SovosTypography;
  let {
    classes,
    expandedItems,
    hasCheckbox,
    isExpandable,
    isTopLevel = false,
    item,
    loadingMessage = 'Loading Items...',
    onClick,
    onExpand,
    parentId = '',
    selectedItems,
    showLessTooltipText,
    showMoreTooltipText
  } = _ref6;
  const {
    t
  } = useMosaicTranslation();
  const isDivider = item.type === itemTypes.HEADER || item.type === itemTypes.DIVIDER;
  const isDisabled = item.disabled === true;
  const parent = parentId ? "".concat(parentId, "_") : '';
  const id = "".concat(parent).concat(item.id);
  if (!isTopLevel && isDivider) {
    console.error('Headers and dividers are only allowed at the top level');
    return null;
  }
  const isExpanded = () => expandedItems[id];
  const renderToggle = expanded => {
    if (!isExpandable) {
      return null;
    }
    function handleKeyUp(e) {
      if (e.key === 'ArrowRight' && !expanded) {
        onExpand(e, id, item);
      }
      if (e.key === 'ArrowLeft' && expanded) {
        onExpand(e, id, item);
      }
    }
    const labelText = expanded ? showLessTooltipText || t('showLess') : showMoreTooltipText || t('showMore');
    if (item.nestedItems || item.nestedItemCount) {
      return /*#__PURE__*/React.createElement(ToggleButton, {
        "aria-expanded": expanded ? 'true' : 'false',
        className: classes === null || classes === void 0 ? void 0 : classes.toggleBtn,
        "data-testid": "sovosTreeListItem__toggle",
        onKeyUp: e => handleKeyUp(e),
        onClick: e => onExpand(e, id, item),
        size: "small",
        tooltipText: labelText
      }, _ArrowRight || (_ArrowRight = /*#__PURE__*/React.createElement(ArrowRight, null)));
    }
    return null;
  };
  const renderNestedItems = () => {
    const expanded = isExpanded();
    if (item.nestedItems && expanded) {
      return /*#__PURE__*/React.createElement(NestedItems, {
        className: classes === null || classes === void 0 ? void 0 : classes.nestedItem
      }, item.nestedItems.map(nItem => /*#__PURE__*/React.createElement(TreeListItem, {
        hasCheckbox: hasCheckbox,
        key: nItem.id,
        item: nItem,
        selectedItems: selectedItems,
        onClick: onClick,
        onExpand: onExpand,
        parentId: id,
        expandedItems: expandedItems,
        classes: classes,
        isExpandable: !!(nItem.nestedItems || nItem.nestedItemCount),
        loadingMessage: loadingMessage
      })));
    }
    if (item.isLoading && expanded) {
      return /*#__PURE__*/React.createElement(Loading, {
        className: classes === null || classes === void 0 ? void 0 : classes.loading
      }, _LinearProgress || (_LinearProgress = /*#__PURE__*/React.createElement(_LinearProgress2, null)), _SovosTypography || (_SovosTypography = /*#__PURE__*/React.createElement(SovosTypography, {
        color: "text.secondary",
        variant: "body1"
      }, loadingMessage)));
    }
    return null;
  };
  const handleClick = isDisabled || item.isExpandOnly ? undefined : () => onClick(id, item);
  const isSelected = selectedItems.includes(id);
  const noHover = isDisabled || !onClick;
  return /*#__PURE__*/React.createElement(ListItem, {
    className: clsx(classes === null || classes === void 0 ? void 0 : classes.listItem, 'sovosTreeListItem'),
    "data-testid": "sovosTreeListItem"
  }, isDivider ? /*#__PURE__*/React.createElement(SectionDivider, {
    content: item.content
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, renderToggle(isExpanded()), /*#__PURE__*/React.createElement(ItemButton, {
    className: clsx('sovosTreeListItem__ItemButton', {
      'sovosTreeListItem__ItemButton--selected': isSelected,
      [classes === null || classes === void 0 ? void 0 : classes.selectedItem]: (classes === null || classes === void 0 ? void 0 : classes.selectedItem) && isSelected,
      'sovosTreeListItem__ItemButton--noHover': noHover,
      [classes === null || classes === void 0 ? void 0 : classes.noHover]: (classes === null || classes === void 0 ? void 0 : classes.noHover) && noHover,
      'sovosTreeListItem__ItemButton--disabled': isDisabled,
      [classes === null || classes === void 0 ? void 0 : classes.disabledItem]: (classes === null || classes === void 0 ? void 0 : classes.disabledItem) && isDisabled
    }),
    classes: {
      root: classes === null || classes === void 0 ? void 0 : classes.item,
      focusVisible: classes === null || classes === void 0 ? void 0 : classes.itemFocusVisible
    },
    component: "button",
    disabled: isDisabled || !onClick,
    disableRipple: true,
    disableTouchRipple: true,
    isExpandable: isExpandable,
    onClick: handleClick
  }, /*#__PURE__*/React.createElement(ItemText, {
    className: clsx('sovosTreeListItem__text', classes === null || classes === void 0 ? void 0 : classes.itemText),
    isExpandable: isExpandable
  }, hasCheckbox && /*#__PURE__*/React.createElement(SovosCheckbox, {
    checked: isSelected,
    size: "small"
  }), typeof item.content === 'string' ? /*#__PURE__*/React.createElement(SovosFixedWidthText, {
    text: item.content,
    className: "sovosTreeListText"
  }) : item.content)), renderNestedItems()));
};
const treeListItemShape = {
  content: props => {
    const {
      content,
      type
    } = props;
    if (type !== itemTypes.DIVIDER && !content) {
      return new Error('The prop `item.content` is required when type is not DIVIDER.');
    }
    return null;
  },
  disabled: bool,
  id: string.isRequired,
  isLoading: bool,
  nestedItemCount: number,
  selected: bool,
  type: oneOf([...Object.values(itemTypes)])
};
treeListItemShape.nestedItems = arrayOf(shape(treeListItemShape));
export const treeListItemPropTypes = shape(treeListItemShape);
TreeListItem.propTypes = {
  classes: object.isRequired,
  expandedItems: object.isRequired,
  hasCheckbox: bool.isRequired,
  isExpandable: bool.isRequired,
  isTopLevel: bool,
  item: treeListItemPropTypes.isRequired,
  loadingMessage: string,
  onClick: func,
  onExpand: func.isRequired,
  parentId: string,
  selectedItems: array.isRequired,
  showLessTooltipText: string,
  showMoreTooltipText: string
};
export default TreeListItem;